import ChatGPTService from '@src/services/ChatGPTService';
import { useDatoClient } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { initializeContentStudioContent } from '@src/utility/general';
import { LogManager } from '@src/utility/Logger';
import { reaction } from 'mobx';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { AIPrompt } from '@src/types.ts/ai_prompts';

import { parseOutlineFromResponse } from './outlineParser';

import type {
  ReelChatMessage,
  ReelOutline,
  ReelProducerConfig,
} from './types';

const logger = LogManager.getLogger('ReelProducer:useReelOutlineGeneration');

/**
 * Build the custom instructions string for the AI prompt from the reel config.
 * Calculates clip ranges and chapter counts based on the target duration.
 */
function buildOutlineInstructions(config: ReelProducerConfig): string {
  let avgClipSec: number;
  let clipRange: string;
  if (config.targetDuration <= 60) {
    avgClipSec = 12;
    clipRange = '10-15';
  } else if (config.targetDuration <= 120) {
    avgClipSec = 20;
    clipRange = '15-25';
  } else {
    avgClipSec = 22;
    clipRange = '15-30';
  }

  const numChapters = Math.max(1, Math.min(8, Math.round(config.targetDuration / avgClipSec)));
  const perBeatDuration = Math.round(config.targetDuration / numChapters);

  return [
    `REEL_DESCRIPTION: ${config.description}`,
    `TARGET_DURATION: ${config.targetDuration}`,
    `NUM_CHAPTERS: ${numChapters}`,
    `PER_BEAT_DURATION: ${perBeatDuration}`,
    `CLIP_RANGE: ${clipRange} seconds per clip`,
  ].join('\n');
}

function createMessageId(): string {
  return `reel-msg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

type UseReelOutlineGenerationResult = {
  outline: ReelOutline | null;
  messages: ReelChatMessage[];
  isLoading: boolean;
  rawResponse: string;
  generate: (config: ReelProducerConfig) => Promise<void>;
  sendMessage: (text: string) => Promise<void>;
};

export function useReelOutlineGeneration(): UseReelOutlineGenerationResult {
  const videoCreator = useVideoCreatorStore();
  const datoClientStore = useDatoClient();

  const gptServiceRef = useRef(
    new ChatGPTService(videoCreator, datoClientStore.aiPromptRepository),
  );
  const gptService = gptServiceRef.current;

  const [outline, setOutline] = useState<ReelOutline | null>(null);
  const [messages, setMessages] = useState<ReelChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [rawResponse, setRawResponse] = useState('');

  // Track config and template for regeneration calls
  const configRef = useRef<ReelProducerConfig | null>(null);
  const templateRef = useRef<AIPrompt | null>(null);
  const lastProcessedResponseRef = useRef<string>('');

  // Observe streaming content and parse when done
  useEffect(() => {
    const dispose = reaction(
      () => ({
        content:
          videoCreator.contentStudioGeneratedContent?.['Reel Outline']?.content
            ?.response ?? '',
        generationState:
          videoCreator.selectedGeneratedContent.reelOutline?.type ?? null,
      }),
      ({ content, generationState }) => {
        const responseText =
          typeof content === 'string' ? content : '';

        if (responseText) {
          setRawResponse(responseText);
        }

        // When generation completes, parse the JSON (guard against duplicate processing)
        if (generationState === 'generated' && responseText && responseText !== lastProcessedResponseRef.current) {
          lastProcessedResponseRef.current = responseText;
          const parsed = parseOutlineFromResponse(responseText);
          if (parsed) {
            setOutline(parsed);
            setMessages((prev) => [
              ...prev,
              {
                id: createMessageId(),
                role: 'assistant',
                content: `Generated outline: "${parsed.title}" with ${parsed.chapters.length} chapter(s).`,
              },
            ]);
            logger.info(
              `Parsed reel outline: "${parsed.title}", ${parsed.chapters.length} chapters`,
            );
          } else {
            logger.warn('Failed to parse reel outline from AI response');
            setMessages((prev) => [
              ...prev,
              {
                id: createMessageId(),
                role: 'assistant',
                content:
                  'I had trouble generating a structured outline. Please try again or provide more details.',
              },
            ]);
          }
        }
      },
    );

    return () => dispose();
  }, [videoCreator]);

  const generate = useCallback(
    async (config: ReelProducerConfig) => {
      configRef.current = config;

      const customInstructions = buildOutlineInstructions(config);

      // Initialize content studio content for this key
      initializeContentStudioContent(
        videoCreator,
        videoCreator.story?.id,
        'Reel Outline',
      );

      setIsLoading(true);
      setOutline(null);
      setRawResponse('');

      // Add initial message with generation details (like article chat)
      setMessages([
        {
          id: createMessageId(),
          role: 'assistant',
          content: 'Generating your reel outline...',
          generationDetails: {
            description: config.description,
            targetDuration: config.targetDuration,
            speakers: config.selectedSpeakers.map(s => s.name).filter(Boolean) as string[],
            goal: config.selectedGoal?.label ?? undefined,
            audiences: config.selectedAudiences.map(a => a.label).filter(Boolean) as string[],
          },
        },
      ]);

      try {
        // Fetch the AI Prompt by its actual Dato title
        const template = await datoClientStore.aiPromptRepository.getPromptByTitle('Custom Reel Outline');
        if (!template) {
          throw new Error('AI Prompt "Custom Reel Outline" not found in DatoCMS');
        }
        templateRef.current = template;

        // Always pass allSpeakers so the transcript includes speaker labels
        // even when the user didn't enable speaker filtering. The AI needs to
        // see who is talking to diversify speakers across beats.
        const speakersForTranscript = config.allSpeakers.length > 0
          ? config.allSpeakers
          : config.selectedSpeakers;

        await gptService.regenerateStreamResponse({
          key: 'Reel Outline',
          type: 'new',
          setLoading: setIsLoading,
          template,
          customInstructions,
          selectedSpeakers: speakersForTranscript,
          selectedGoal: config.selectedGoal,
          selectedAudiences: config.selectedAudiences,
        });
      } catch (error) {
        const errMsg =
          error instanceof Error ? error.message : String(error);
        logger.warn(`Reel outline generation failed: ${errMsg}`);
        setMessages((prev) => [
          ...prev,
          {
            id: createMessageId(),
            role: 'assistant',
            content: `Generation failed: ${errMsg}. Please try again.`,
          },
        ]);
        setIsLoading(false);
      }
    },
    [videoCreator, gptService],
  );

  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim() || isLoading) return;

      const config = configRef.current;

      // Add user message
      setMessages((prev) => [
        ...prev,
        {
          id: createMessageId(),
          role: 'user',
          content: text,
        },
      ]);

      setIsLoading(true);

      const customInstructions = config
        ? buildOutlineInstructions(config)
        : undefined;

      try {
        await gptService.regenerateStreamResponse({
          key: 'Reel Outline',
          type: 'regenerate',
          setLoading: setIsLoading,
          template: templateRef.current ?? undefined,
          revision: text,
          customInstructions,
          selectedSpeakers: config?.allSpeakers?.length
            ? config.allSpeakers
            : config?.selectedSpeakers,
          selectedGoal: config?.selectedGoal,
          selectedAudiences: config?.selectedAudiences,
        });
      } catch (error) {
        const errMsg =
          error instanceof Error ? error.message : String(error);
        logger.warn(`Reel outline refinement failed: ${errMsg}`);
        setMessages((prev) => [
          ...prev,
          {
            id: createMessageId(),
            role: 'assistant',
            content: `Refinement failed: ${errMsg}. Please try again.`,
          },
        ]);
        setIsLoading(false);
      }
    },
    [isLoading, gptService],
  );

  return { outline, messages, isLoading, rawResponse, generate, sendMessage };
}
